export const ErrorCodes = {
  // Legacy error codes (keeping for backward compatibility)
  COMMAND_NOT_FOUND: 100,
  TOKEN_EXPIRED: 101,
  ACCESS_DENIED: 102,
  MISSING_PARAMETERS: 103,
  WRONG_SYSTEM_TOKEN: 104,
  CANNOT_ACCESS_GUEST_ACCOUNT: 105,
  WRONG_JSON_FORMAT: 106,

  PWD_INCORRECT: 201,
  USER_NOT_FOUND: 202,
  INCORRECT_ORIGINAL_PWD: 203,
  CANNOT_DEL_ACCOUNT: 204,
  USER_EXISTED: 205,
  USER_BLOCKED: 206,
  USER_NOT_ACTIVATE: 209,
  PASSWORD_TOO_SHORT: 210,
  USER_CANNOT_BE_DELETED_IN_UAT: 211,

  // New Response Status Codes (matching backend enum)
  OPERATING_SUCCESSFULLY: '0000',
  EMPTY_CONTENT: '0001',
  NOTHING_CHANGED: '0002',
  OPERATING_FAILED: '2000',
  ILLEGAL_PARAMETERS: '2001',
  UNAUTHORIZED: '2002',
  FORBIDDEN: '2003',
  NOT_FOUND: '2004',
  METHOD_NOT_ALLOWED: '2005',
  REQUEST_TIMEOUT: '2006',
  SYSTEM_ERROR: '3000',

  DEAL_EXIST: 301,
  DEAL_NOT_FIND: 302,
  DEAL_ALREADY_IN_FAV_LIST: 303,

  DELIVERY_ADDRESS_EXIST: 401,
  DELIVERY_ADDRESS_NOT_FIND: 402,
  DELIVERY_ADDRESS_MORE_THAN_FIVE: 403,

  VOUCHER_EXIST: 501,
  VOUCHER_NOT_FIND: 502,
  VOUCHER_NOT_OWNED: 503,

  ERR_WRONG_COMING_STATUS: 563,
  ERR_GROUP_NOT_IN_SAME_STATUS: 565,
  ERR_FLAG_BAG_NOT_SPARE: 566,
  ERR_FLAG_BAG_NOT_IN_SAME_STATION: 567,

  ERR_FLAG_BAG_NOT_IN_EVENT: 573,

  ERR_FLAG_BAG_WRONG_STATUS: 578,

  REMINDER_NOT_FIND: 602,

  WAVE_SERIAL_NUM_NOT_FIND: 702,
  WAVE_SERIAL_NUM_IS_USED: 703,
  WAVE_SERIAL_NUM_REGISTERED: 704,

  STATION_NOT_FOUND: 801,
  EVENT_NOT_FOUND: 802,

  QTY_MUST_LARGER_THAN_ZERO: 905,
  QTY_NOT_ENOUGH: 906,
  QTY_EXCESS_MAX: 907,

  OTHER: 999,
  NETWORK_ERROR: 1001,
  OFFLINE_MODE: 1002,
  LOCAL_ERROR: -1,
} as const;

export const HTTP_ERROR_CODES = [
  '400', '401', '402', '403', '404', '405', 
  '500', '501', '502', '503', '504'
] as const;

export const isErrorResponse = (response: { 
  category?: string; 
  code?: string | number;
  message?: string;
}): boolean => {
  if (!response) return false;
  
  // Check response code (primary indicator)
  if (response.code) {
    const codeStr = response.code.toString();
    
    // Success codes (0xxx range)
    // 0000: Operating successfully
    // 0001: Empty content (still success)
    // 0002: Nothing changed (still success)
    if (codeStr.startsWith('0') && codeStr.length === 4) {
      return false;
    }
    
    // Error codes (2xxx, 3xxx ranges)
    // 2000: Operating failed
    // 2001: Illegal parameters
    // 2002: Unauthorized
    // 2003: Forbidden
    // 2004: Not found
    // 2005: Method not allowed
    // 2006: Request timeout
    // 3000: System error
    if (codeStr.startsWith('2') || codeStr.startsWith('3')) {
      return true;
    }
    
    // HTTP error codes (400, 401, 403, 404, 500, etc.)
    if (HTTP_ERROR_CODES.includes(codeStr as any)) {
      return true;
    }
    
    // Legacy error codes (for backward compatibility)
    const codeNum = Number(codeStr);
    if (codeNum === 101 || // Token expired
        (codeNum >= 500 && codeNum < 600) || // Business logic errors
        (codeNum >= 800 && codeNum < 900)) {  // Not found errors
      return true;
    }
  }
  
  // Check if category explicitly indicates an error
  if (response.category) {
    const category = response.category.toUpperCase();
    if (category === 'ERROR') {
      return true;
    }
  }
  
  return false;
};

export const ErrorMessages: Record<string | number, string> = {
  // Legacy error messages
  [ErrorCodes.TOKEN_EXPIRED]: '登入逾時，請重新登入',
  [ErrorCodes.COMMAND_NOT_FOUND]: '指令不存在',
  [ErrorCodes.ACCESS_DENIED]: '權限不足',
  [ErrorCodes.MISSING_PARAMETERS]: '缺少參數',
  [ErrorCodes.WRONG_SYSTEM_TOKEN]: '系統權杖錯誤',
  [ErrorCodes.CANNOT_ACCESS_GUEST_ACCOUNT]: '無法存取客戶帳號',
  [ErrorCodes.WRONG_JSON_FORMAT]: 'JSON格式錯誤',

  [ErrorCodes.PWD_INCORRECT]: '登入密碼不正確',
  [ErrorCodes.USER_NOT_FOUND]: '登入名稱不存在',
  [ErrorCodes.INCORRECT_ORIGINAL_PWD]: '和原密碼相同',
  [ErrorCodes.CANNOT_DEL_ACCOUNT]: '無法刪除帳號',
  [ErrorCodes.USER_EXISTED]: '用戶已存在',
  [ErrorCodes.USER_BLOCKED]: '用戶已被封鎖',
  [ErrorCodes.USER_NOT_ACTIVATE]: '用戶未啟用',
  [ErrorCodes.PASSWORD_TOO_SHORT]: '密碼長度太短',
  [ErrorCodes.USER_CANNOT_BE_DELETED_IN_UAT]: 'UAT環境無法刪除帳號',

  // New Response Status Code error messages
  [ErrorCodes.OPERATING_FAILED]: '操作失敗',
  [ErrorCodes.ILLEGAL_PARAMETERS]: '參數錯誤',
  [ErrorCodes.UNAUTHORIZED]: '未授權，請重新登入',
  [ErrorCodes.FORBIDDEN]: '禁止訪問',
  [ErrorCodes.NOT_FOUND]: '資源不存在',
  [ErrorCodes.METHOD_NOT_ALLOWED]: '請求方法不允許',
  [ErrorCodes.REQUEST_TIMEOUT]: '請求超時',
  [ErrorCodes.SYSTEM_ERROR]: '系統錯誤',

  [ErrorCodes.DEAL_EXIST]: '交易已存在',
  [ErrorCodes.DEAL_NOT_FIND]: '交易不存在',
  [ErrorCodes.DEAL_ALREADY_IN_FAV_LIST]: '交易已在收藏清單中',

  [ErrorCodes.DELIVERY_ADDRESS_EXIST]: '收貨地址已存在',
  [ErrorCodes.DELIVERY_ADDRESS_NOT_FIND]: '收貨地址不存在',
  [ErrorCodes.DELIVERY_ADDRESS_MORE_THAN_FIVE]: '最多只能有5個收貨地址',

  [ErrorCodes.VOUCHER_EXIST]: '優惠券已存在',
  [ErrorCodes.VOUCHER_NOT_FIND]: '優惠券不存在',
  [ErrorCodes.VOUCHER_NOT_OWNED]: '優惠券不存在',

  [ErrorCodes.ERR_WRONG_COMING_STATUS]: '旗袋狀態不正確',
  [ErrorCodes.ERR_GROUP_NOT_IN_SAME_STATUS]: '組別狀態不正確',
  [ErrorCodes.ERR_FLAG_BAG_NOT_SPARE]: '旗袋不可使用',
  [ErrorCodes.ERR_FLAG_BAG_NOT_IN_SAME_STATION]: '旗袋不在同一站',

  [ErrorCodes.ERR_FLAG_BAG_NOT_IN_EVENT]: '旗袋不在活動中',

  [ErrorCodes.ERR_FLAG_BAG_WRONG_STATUS]: '旗袋狀態不正確',

  [ErrorCodes.REMINDER_NOT_FIND]: '提醒不存在',

  [ErrorCodes.WAVE_SERIAL_NUM_NOT_FIND]: '波次序號不存在',
  [ErrorCodes.WAVE_SERIAL_NUM_IS_USED]: '波次序號已使用',
  [ErrorCodes.WAVE_SERIAL_NUM_REGISTERED]: '波次序號已註冊',

  [ErrorCodes.STATION_NOT_FOUND]: '旗站不存在',
  [ErrorCodes.EVENT_NOT_FOUND]: '活動不存在',

  [ErrorCodes.QTY_MUST_LARGER_THAN_ZERO]: '數量必須大於0',
  [ErrorCodes.QTY_NOT_ENOUGH]: '數量不足',
  [ErrorCodes.QTY_EXCESS_MAX]: '數量超過上限',

  [ErrorCodes.OTHER]: '其他錯誤',
  [ErrorCodes.NETWORK_ERROR]: '網路錯誤',
  [ErrorCodes.OFFLINE_MODE]: '離線模式',
  [-1]: '本地數據錯誤',
};
